/**
 * client/src/utils/api.js
 * 
 * Utility for making API calls with axios.
 * Centralized API configuration for the application.
 */
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { getSecurityHeaders } from './securityUtils';

// Resolve API base URL across environments (staging/prod/dev)
const getBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && envUrl !== '/api') {
    return envUrl.replace(/\/$/, '');
  }
  // Host-based fallback if env not set or set to '/api'
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host.includes('staging')) return 'https://api.staging.mnymny.com';
    if (host.includes('mnymny.com')) return 'https://api.mnymny.com';
  }
  // Local/dev fallback (relative to same origin proxy)
  return '';
};

// Create axios instance with base URL and default headers
const api = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    ...getSecurityHeaders()
  }
});

// Debug: log resolved API base URL once at module load
try {
  // eslint-disable-next-line no-console
  console.log('[api] baseURL ->', api.defaults.baseURL || '(empty)');
} catch (_) {}

// Add request interceptor to attach auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Registration success but needs verification - don't treat as error
    if (error.response?.status === 403 && error.response?.data?.requiresVerification) {
      console.log('🔐 Email verification required');
      
      // If we're already on the verification page, don't redirect
      const isOnVerificationPage = window.location.pathname.includes('/verify-email');
      if (!isOnVerificationPage) {
        console.log('🔄 Redirecting to verification page');
        window.location.href = '/verify-email';
      }
      return Promise.reject(error);
    }
    
    // Handle unauthorized errors
    if (error.response?.status === 401) {
      console.log('🔒 Unauthorized: Clearing auth data...');
      
      // Check if this is a login or register attempt
      const isAuthEndpoint = error.config.url.includes('/login') || 
                           error.config.url.includes('/register') ||
                           error.config.url.includes('/verify-code');
      
      // Only clear token and redirect for non-auth endpoints
      if (!isAuthEndpoint) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Redirect to login if not already there
        if (!window.location.pathname.includes('/login')) {
          toast.error('Your session has expired. Please login again.');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api; 